import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Plus, Search, RefreshCw, ChevronLeft, ChevronRight, Users, AlertCircle } from 'lucide-react';
import { fetchCampaigns, fetchNotOnboardedUsers } from '../services/campaignService';
import { fetchEmailOutbox, retryOutboxEmail } from '../services/outboxService';
import CreateCampaign from './campaigns/CreateCampaign';
import CampaignDetail from './campaigns/CampaignDetail';
import EmailDeliveryPanel from './campaigns/EmailDeliveryPanel';

const STATUS_BADGES = {
  draft:      { label: 'Draft',     bg: 'bg-gray-100',    text: 'text-gray-700' },
  test_sent:  { label: 'Test Sent', bg: 'bg-blue-100',    text: 'text-blue-700' },
  confirmed:  { label: 'Confirmed', bg: 'bg-yellow-100',  text: 'text-yellow-700' },
  sending:    { label: 'Sending',   bg: 'bg-indigo-100',  text: 'text-indigo-700' },
  paused:     { label: 'Paused',    bg: 'bg-orange-100',  text: 'text-orange-700' },
  completed:  { label: 'Completed', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  failed:     { label: 'Failed',    bg: 'bg-red-100',     text: 'text-red-700' },
};

const TYPE_LABELS = {
  software_launch: 'Software Launch',
  onboarding:      'Onboarding',
  offboarding:     'Offboarding',
  new_features:    'New Features',
  custom:          'Custom',
};

const EmailCampaigns = () => {
  const [view, setView]             = useState('list'); // 'list' | 'create' | 'detail'
  const [selectedId, setSelectedId] = useState(null);
  const [createPreset, setCreatePreset] = useState(null); // pre-fill type/recipients when opening create
  const [campaigns, setCampaigns]   = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [isLoading, setIsLoading]   = useState(false);
  const [error, setError]           = useState('');

  // Filters
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter]     = useState('');

  // Onboarding alert
  const [notOnboardedCount, setNotOnboardedCount] = useState(0);

  // Undelivered system emails (welcome notices and the like)
  const [outboxSummary, setOutboxSummary] = useState(null);
  const [outboxItems, setOutboxItems]     = useState([]);

  // Reusable: refresh the not-onboarded banner count
  const refreshOnboardingCount = useCallback(() => {
    fetchNotOnboardedUsers()
      .then(data => setNotOnboardedCount(data.total || 0))
      .catch(() => {}); // non-critical
  }, []);

  const refreshOutbox = useCallback(() => {
    fetchEmailOutbox()
      .then(data => {
        setOutboxSummary(data?.summary || null);
        setOutboxItems(data?.items || []);
      })
      .catch(() => {}); // non-critical — never block the campaign list
  }, []);

  // Refresh counts whenever the list view is shown (mount, or returning from detail/create)
  useEffect(() => {
    if (view === 'list') {
      refreshOnboardingCount();
      refreshOutbox();
    }
  }, [view, refreshOnboardingCount, refreshOutbox]);

  const loadCampaigns = useCallback(async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const data = await fetchCampaigns({
        page,
        limit: pagination.limit,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        search: search || undefined,
      });
      setCampaigns(data.campaigns || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
    } catch (err) {
      setError(err.message || 'Failed to load campaigns');
    } finally {
      setIsLoading(false);
    }
  }, [pagination.limit, statusFilter, typeFilter, search]);

  useEffect(() => {
    if (view === 'list') {
      const timer = setTimeout(() => loadCampaigns(1), 300);
      return () => clearTimeout(timer);
    }
  }, [view, statusFilter, typeFilter, search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh while any campaign is still sending — keeps the list and
  // onboarding banner count up to date as the queue processes emails
  useEffect(() => {
    if (view !== 'list') return;
    const hasSending = campaigns.some(c => c.status === 'sending');
    if (!hasSending) return;

    const interval = setInterval(() => {
      loadCampaigns(pagination.page);
      refreshOnboardingCount();
    }, 5000);
    return () => clearInterval(interval);
  }, [view, campaigns, pagination.page, loadCampaigns, refreshOnboardingCount]);

  const handleViewDetail = (id) => {
    setSelectedId(id);
    setView('detail');
  };

  const handleBack = () => {
    setView('list');
    setSelectedId(null);
  };

  const handleCampaignCreated = () => {
    setView('list');
    loadCampaigns(1);
    refreshOnboardingCount();
  };

  // ─── Create Campaign View ────────────────────────────────────────────────
  if (view === 'create') {
    return <CreateCampaign onBack={handleBack} onCreated={handleCampaignCreated} preset={createPreset} />;
  }

  // ─── Campaign Detail View ────────────────────────────────────────────────
  if (view === 'detail' && selectedId) {
    return <CampaignDetail campaignId={selectedId} onBack={handleBack} />;
  }

  // ─── Campaign List View ──────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Mail className="w-7 h-7 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email Campaigns</h1>
            <p className="text-sm text-gray-500">Send targeted emails to physicians and users</p>
          </div>
        </div>
        <button
          onClick={() => { setCreatePreset(null); setView('create'); }}
          className="flex items-center space-x-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          <span>New Campaign</span>
        </button>
      </div>

      {/* Undelivered system emails — hidden entirely when there is nothing wrong */}
      <EmailDeliveryPanel
        summary={outboxSummary}
        items={outboxItems}
        onRetry={async (id) => {
          const result = await retryOutboxEmail(id);
          refreshOnboardingCount();
          return result;
        }}
        onRefresh={refreshOutbox}
      />

      {/* Onboarding Alert Banner */}
      {notOnboardedCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-800">
                {notOnboardedCount} user{notOnboardedCount !== 1 ? 's' : ''} awaiting onboarding
              </p>
              <p className="text-xs text-amber-600 mt-0.5">
                These users have accounts but have never received a welcome or onboarding email.
              </p>
            </div>
          </div>
          <button
            onClick={() => { setCreatePreset({ type: 'onboarding', recipientFilter: 'not_onboarded' }); setView('create'); }}
            className="flex items-center space-x-1.5 px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-xs font-medium flex-shrink-0 ml-4"
          >
            <Mail className="w-3.5 h-3.5" />
            <span>Send Onboarding Email</span>
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by subject..."
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="test_sent">Test Sent</option>
            <option value="confirmed">Confirmed</option>
            <option value="sending">Sending</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">All Types</option>
            <option value="software_launch">Software Launch</option>
            <option value="onboarding">Onboarding</option>
            <option value="offboarding">Offboarding</option>
            <option value="new_features">New Features</option>
            <option value="custom">Custom</option>
          </select>

          <button
            onClick={() => { loadCampaigns(pagination.page); refreshOnboardingCount(); }}
            className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading && campaigns.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-16">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No campaigns yet</p>
            <p className="text-gray-400 text-sm mt-1">Create your first campaign to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Subject</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Recipients</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Sent</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Failed</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {campaigns.map((c) => {
                  const badge = STATUS_BADGES[c.status] || STATUS_BADGES.draft;
                  return (
                    <tr
                      key={c.id}
                      onClick={() => handleViewDetail(c.id)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3.5">
                        <p className="text-sm font-medium text-gray-900 truncate max-w-[250px]">{c.subject}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-sm text-gray-600">{TYPE_LABELS[c.type] || c.type}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="text-sm text-gray-700 font-medium">{c.total_recipients}</span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="text-sm text-emerald-600 font-medium">{c.sent_count}</span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className={`text-sm font-medium ${c.failed_count > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {c.failed_count}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-sm text-gray-500">
                          {new Date(c.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-sm text-gray-500 truncate max-w-[100px] block">{c.created_by_name}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">
              Showing {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => loadCampaigns(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="p-1.5 rounded border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => loadCampaigns(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="p-1.5 rounded border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailCampaigns;
